import {useRouter} from 'next/router';
import PropTypes from 'prop-types';
import {memo, useCallback, useContext, useMemo} from 'react';

import {SEARCH_FILTERS} from '@/src/app/constants/search';
import useIsMobile from '@/src/hooks/useIsMobile';
import TextLocal from '@/src/textLocal/containers/TextLocal';

import PopupContext from '../../popup/utils/PopupContext';
import {Button, Separator} from '../../ui';
import FilterListContainer from '../containers/FilterListContainer';
import css from '../styles/FiltersLayout.module.css';
import {useSearchFilters} from '../utils/useSearchFilters';
import FilterRow from './FilterRow';
import FiltersModal from './FiltersModal';

const NESTED_FILTERS_COUNT = 4;

const FiltersLayout = ({userType}) => {
  const {query} = useRouter();
  const {isMaxWidth900} = useIsMobile();
  const {openPopup, closePopup} = useContext(PopupContext);
  const {handleClearAllFiltersClick} = useSearchFilters(userType);

  const appliedFiltersCount = useMemo(() => {
    /**
     * By default we have 4 filters that we nest.
     */
    let nestedFiltersCounter = NESTED_FILTERS_COUNT;

    /**
     * Also we need not to count last name and first name filters because we use them only in UI purposes.
     */
    nestedFiltersCounter += query[SEARCH_FILTERS.FIRST_NAME] ? 1 : 0;
    nestedFiltersCounter += query[SEARCH_FILTERS.LAST_NAME] ? 1 : 0;
    nestedFiltersCounter += query[SEARCH_FILTERS.CLINIC_ID] ? 1 : 0;

    return Object.keys(query).length - nestedFiltersCounter;
  }, [query]);

  const handleOpenModalClick = useCallback(() => {
    // @ts-ignore
    openPopup({
      popup: (
        <FiltersModal
          filters={<FilterListContainer userType={userType} />}
          closePopup={closePopup}
        />
      ),
      options: {withCloseButton: true},
    });
  }, [query]);

  return (
    <aside className={css.filtersContainer}>
      <FilterRow isHeading>
        {isMaxWidth900 && (
          <Button
            height={Button.HEIGHT.SMALL}
            color={Button.COLOR.SECONDARY}
            onClick={handleOpenModalClick}
          >
            <TextLocal id={'search.filters'} />
            {appliedFiltersCount > 0 && (
              <span className={css.filterBadge}>{appliedFiltersCount}</span>
            )}
          </Button>
        )}
        <button
          onClick={handleClearAllFiltersClick}
          className={css.clearAllButton}
        >
          <TextLocal id={'search.clearAll'} />
        </button>
      </FilterRow>

      <FilterRow>
        <Separator />
      </FilterRow>

      {!isMaxWidth900 && <FilterListContainer userType={userType} />}
    </aside>
  );
};

FiltersLayout.propTypes = {
  userType: PropTypes.string,
};

export default memo(FiltersLayout);
